import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  useColorModeValue,
  useColorMode,
  Badge,
  List,
  ListItem,
  ListIcon,
  Divider,
} from '@chakra-ui/react'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  FaCheckCircle,
  FaGift,
  FaClock,
  FaRocket,
  FaUsers,
  FaChartBar,
} from 'react-icons/fa'
import LEColorConstants, {
  basicTextColor,
} from '@/lib/constants/colorConstants'
import CustomButton from '../Button'
import router from 'next/router'

export const SpecialOfferCTA = () => {
  const { colorMode } = useColorMode()
  const bgColor = useColorModeValue('white', 'gray.900')

  const controls = useAnimation()
  const { inView, ref } = useInView({ threshold: 0.3 })

  // Countdown timer for urgency (24 hours from now)
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 45,
  })

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  // Simulate countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 }
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 }
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 }
        }
        return prev
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const goToSignUp = () => {
    const host =
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
        ? process.env.NEXT_PUBLIC_APP_HOST
        : process.env.NEXT_PUBLIC_APP_LOCALHOST

    return router.push(host + '/signup')
  }

  const offerFeatures = [
    {
      icon: FaGift,
      title: '500 FREE Credits',
      description:
        'Worth ₦25,000 - Test your entire school with no upfront cost',
    },
    // {
    //   icon: FaUsers,
    //   title: 'Unlimited Students',
    //   description:
    //     'No per-student fees - use all 500 credits across your entire school',
    // },
    {
      icon: FaChartBar,
      title: 'Complete Analytics',
      description: 'Detailed performance reports for every student and subject',
    },
    {
      icon: FaRocket,
      title: 'Instant Setup',
      description: '5-minute setup process - start testing immediately',
    },
  ]

  return (
    <Box
      bg={bgColor}
      py="5rem"
      ref={ref}
      as={motion.div}
      animate={controls}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      position="relative"
      overflow="hidden"
    >
      {/* Background Pattern */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgImage="radial-gradient(circle at 25% 25%, rgba(0, 155, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255, 110, 6, 0.1) 0%, transparent 50%)"
        zIndex="0"
      />

      <Container
        maxW="90rem"
        mx="auto"
        px={['1rem', '3rem', '4rem', '6rem']}
        position="relative"
        zIndex="1"
      >
        <VStack spacing="3rem">
          {/* Limited Time Banner */}
          <Box
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: -30 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            <Badge
              colorScheme="red"
              variant="solid"
              fontSize={{ base: '0.9rem', md: '1.2rem' }}
              py="0.8rem"
              px="2rem"
              borderRadius="full"
              textAlign="center"
              animation="pulse 2s infinite"
            >
              ⚡ LIMITED TIME OFFER - EXPIRES IN{' '}
              {timeLeft.hours.toString().padStart(2, '0')}:
              {timeLeft.minutes.toString().padStart(2, '0')}:
              {timeLeft.seconds.toString().padStart(2, '0')}
            </Badge>
          </Box>

          {/* Main Offer Box */}
          <Box
            w="full"
            maxW="6xl"
            bg="linear-gradient(135deg, #009BFF 0%, #FF6E06 100%)"
            borderRadius="24px"
            p={['2rem', '3rem', '4rem']}
            textAlign="center"
            position="relative"
            overflow="hidden"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, scale: 0.9 },
              visible: {
                opacity: 1,
                scale: 1,
                transition: { duration: 0.8, delay: 0.2 },
              },
            }}
          >
            {/* Decorative Elements */}
            <Box
              position="absolute"
              top="-50px"
              right="-50px"
              w="200px"
              h="200px"
              borderRadius="full"
              bg="rgba(255, 255, 255, 0.1)"
              as={motion.div}
              animate={
                {
                  rotate: 360,
                } as any
              }
              transition={
                {
                  duration: 20,
                  repeat: Infinity,
                  ease: 'linear',
                } as any
              }
            />

            <Box
              position="absolute"
              bottom="-30px"
              left="-30px"
              w="150px"
              h="150px"
              borderRadius="full"
              bg="rgba(255, 255, 255, 0.1)"
              as={motion.div}
              animate={{
                rotate: -360,
              }}
              transition={
                {
                  duration: 15,
                  repeat: Infinity,
                  ease: 'linear',
                } as any
              }
            />

            <VStack spacing="2rem" position="relative" zIndex="1">
              <Heading
                fontSize={['2.5rem', '3rem', '4rem']}
                fontWeight="bold"
                color="white"
                lineHeight="1.2"
              >
                🎁 Get 500 Credits
                <br />
                <Text as="span" fontSize={['2rem', '2.5rem', '3rem']}>
                  Absolutely FREE!
                </Text>
              </Heading>

              <Text
                fontSize={['1.3rem', '1.5rem', '1.8rem']}
                color="white"
                fontWeight="bold"
                maxW="4xl"
                mx="auto"
              >
                Worth ₦25,000 - Enough to test your ENTIRE SCHOOL for FREE!
              </Text>

              <Text
                fontSize={['1.1rem', '1.2rem', '1.3rem']}
                color="rgba(255, 255, 255, 0.9)"
                maxW="3xl"
                mx="auto"
                lineHeight="1.6"
              >
                This exclusive offer is specifically designed for Nigerian
                schools preparing for the CBT transition. No hidden fees, no
                setup costs.
              </Text>

              {/* What's Included */}
              <Box
                bg="rgba(255, 255, 255, 0.15)"
                borderRadius="16px"
                p="2rem"
                w="full"
                maxW="4xl"
                backdropFilter="blur(10px)"
              >
                <Heading
                  fontSize={['1.5rem', '1.8rem']}
                  color="white"
                  mb="1.5rem"
                >
                  What You Get With Your 500 Free Credits:
                </Heading>

                <List spacing="1rem">
                  {[
                    'Complete CBT practice sessions for up to 500 students',
                    // 'Access to WAEC, NECO, and JAMB-style question banks',
                    'Real-time performance analytics and reporting',
                    'Advanced anti-cheating and monitoring tools',
                    'Dedicated onboarding and training support',
                    '24/7 technical assistance during critical periods',
                  ].map((feature, index) => (
                    <ListItem key={index} display="flex" alignItems="center">
                      <ListIcon
                        as={FaCheckCircle}
                        color="white"
                        boxSize="20px"
                      />
                      <Text
                        color="white"
                        fontSize={['1rem', '1.1rem']}
                        textAlign="left"
                      >
                        {feature}
                      </Text>
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* CTA Button */}
              <CustomButton
                size="lg"
                w={{ base: 'full', sm: 'auto' }}
                minW="20rem"
                h="4rem"
                borderRadius="12px"
                fontWeight="bold"
                fontSize={['0.9rem', '1.2rem']}
                bg="white"
                // color={LEColorConstants.primary}
                onClick={goToSignUp}
                _hover={{
                  transform: 'translateY(-3px) scale(1.05)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                  // bg: 'gray.50',
                }}
                _active={{
                  transform: 'translateY(0) scale(1)',
                }}
                transition="all 0.2s"
                boxShadow="0 10px 30px rgba(0, 0, 0, 0.2)"
              >
                Claim Your 500 FREE Credits Now! 🚀
              </CustomButton>

              <Text fontSize="0.9rem" color="rgba(255, 255, 255, 0.8)">
                ✅ No credit card required • ✅ Instant access • ✅ Cancel
                anytime
              </Text>
            </VStack>
          </Box>

          {/* Feature Grid */}
          <Box
            w="full"
            as={motion.div}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                  delayChildren: 0.4,
                },
              },
            }}
          >
            <Flex wrap="wrap" justify="center" gap="2rem">
              {offerFeatures.map((feature, index) => (
                <Box
                  key={index}
                  bg={useColorModeValue('white', 'gray.800')}
                  p="2rem"
                  borderRadius="16px"
                  boxShadow="lg"
                  border="2px solid"
                  borderColor={LEColorConstants.primary}
                  maxW="280px"
                  textAlign="center"
                  as={motion.div}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.5 },
                    },
                  }}
                  whileHover={{
                    scale: 1.05,
                    transition: { duration: 0.2 },
                  }}
                >
                  <Flex
                    w="64px"
                    h="64px"
                    bg={`${LEColorConstants.primary}15`}
                    borderRadius="16px"
                    align="center"
                    justify="center"
                    mx="auto"
                    mb="1rem"
                  >
                    <FaCheckCircle
                      size="32px"
                      color={LEColorConstants.primary}
                    />
                  </Flex>

                  <Heading
                    size="md"
                    color={basicTextColor[colorMode]}
                    mb="0.5rem"
                  >
                    {feature.title}
                  </Heading>

                  <Text
                    color={basicTextColor[colorMode]}
                    fontSize="0.95rem"
                    lineHeight="1.5"
                  >
                    {feature.description}
                  </Text>
                </Box>
              ))}
            </Flex>
          </Box>

          {/* Urgency Footer */}
          <Box
            textAlign="center"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.6 },
              },
            }}
          >
            <Text
              fontSize={['1.2rem', '1.4rem']}
              fontWeight="bold"
              color={LEColorConstants.secondary}
              mb="1rem"
            >
              ⚠️ This offer expires once 100 schools claim it!
            </Text>

            <Text
              fontSize={['1rem', '1.1rem']}
              color={basicTextColor[colorMode]}
              maxW="3xl"
              mx="auto"
            >
              We're limiting this offer to ensure we can provide exceptional
              support to each school.
              <Text
                as="span"
                fontWeight="bold"
                color={LEColorConstants.primary}
              >
                {' '}
                Only 23 spots remaining
              </Text>{' '}
              - secure yours before it's too late.
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
